import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import Edit from "../../Images/Edit.png";
import Delete from "../../Images/deleteButton-icon.png";
import RoleLeadIndicatorConfiguration from "./RoleLeadIndicatorConfiguration";
import { TargetFunnel, Get_TargetFunnel } from "../../services/userAPI";
import { message } from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";

const scopedStyles = `
  .rl-funnel-color-input::-webkit-color-swatch {
    width: 100%; height: 100%; border-radius: 50%; border: none; padding: 0;
  }
  .rl-funnel-color-input::-webkit-color-swatch-wrapper { padding: 0; border-radius: 50%; }
  .rl-funnel-color-input::-moz-color-swatch {
    width: 100%; height: 100%; border-radius: 50%; border: none; padding: 0;
  }
  .rl-funnel-num-input::-webkit-outer-spin-button,
  .rl-funnel-num-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .rl-funnel-num-input { -moz-appearance: textfield; }
  .rl-funnel-scroll::-webkit-scrollbar { display: none; }
`;

const RoleTargetFunnelConfiguration = ({ onNext, roleId, accountId }) => {
    const [isTargetCollapsed, setIsTargetCollapsed] = useState(false);
    const [isAddingNewTarget, setIsAddingNewTarget] = useState(false);
    const [targets, setTargets] = useState([]);
    const [leadIndicators, setLeadIndicators] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);

    const [targetName, setTargetName] = useState("");
    const [monthlyTarget, setMonthlyTarget] = useState("");
    const [color, setColor] = useState("#add8e6");
    const [errors, setErrors] = useState({});

    const steps = ["Step 1", "Step 2", "Step 3", "Step 4"];
    const [currentStep, setCurrentStep] = useState(0);

    const [saveKpiMasterDetails, { loading }] = useMutation(TargetFunnel);

    const { data, refetch } = useQuery(Get_TargetFunnel, {
        variables: { roleId: roleId },
        skip: !roleId,
        fetchPolicy: "network-only",
    });

    // Format color for display (receiving from API)
    const formatColorForDisplay = (colorValue) => {
        if (!colorValue) return "#add8e6"; // Default color

        let colorCode = colorValue;

        // Remove "FF" if it starts with it
        if (colorCode.toLowerCase().startsWith("ff")) {
            colorCode = colorCode.slice(2);
        }

        // Ensure the color starts with "#"
        if (!colorCode.startsWith("#")) {
            colorCode = `#${colorCode}`;
        }

        return colorCode;
    };

    // Format color for API (sending to API)
    const formatColorForAPI = (colorValue) => {
        if (!colorValue) return "add8e6"; // Default color without #

        // Remove # if present
        let colorCode = colorValue.startsWith("#") ? colorValue.slice(1) : colorValue;

        // Do not add FF prefix when sending to API
        return colorCode;
    };

    useEffect(() => {
        if (data?.getKpisForRole) {
            setTargets(
                data.getKpisForRole.map(kpi => {
                    return {
                        id: kpi.id,
                        name: kpi.name,
                        monthly: kpi.defaultValue,
                        color: formatColorForDisplay(kpi.colorValue),
                    };
                })
            );
        }
    }, [data]);

    // Handler for receiving lead indicators from child component
    const handleLeadIndicatorsChange = (indicators) => {
        setLeadIndicators(indicators);
    };

    const validate = () => {
        let newErrors = {};
        if (!targetName.trim()) newErrors.targetName = "Target Funnel Name is required.";
        if (!monthlyTarget || isNaN(monthlyTarget) || Number(monthlyTarget) <= 0) {
            newErrors.monthlyTarget = "Monthly Target must be a positive number.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const resetForm = () => {
        setIsAddingNewTarget(false);
        setEditingIndex(null);
        setTargetName("");
        setMonthlyTarget("");
        setColor("#add8e6");
        setErrors({});
    };

    const handleAddOrUpdate = async () => {
        if (validate()) {
            try {
                const isEditing = editingIndex !== null;
                const selectedTarget = isEditing ? targets[editingIndex] : null;

                // Format color properly for API
                const brandColor = formatColorForAPI(color);

                const response = await saveKpiMasterDetails({
                    variables: {
                        accountId: accountId,
                        roleId: roleId,
                        kpiId: isEditing ? selectedTarget.id : null, // Send existing kpiId if editing
                        kpiName: targetName,
                        kpiDefaultTarget: Number(monthlyTarget),
                        kpiColorCode: brandColor, // HEX color format without #
                        status: 1 // Active status
                    },
                });

                if (response.data.saveKpiMasterDetails.success) {
                    // After successful API call, update local state properly
                    const newTargetData = response.data.saveKpiMasterDetails.data;
                    const newTarget = {
                        id: newTargetData.id,
                        name: newTargetData.name,
                        monthly: newTargetData.defaultValue,
                        color: formatColorForDisplay(newTargetData.colorValue),
                    };

                    let updatedTargets;
                    if (isEditing) {
                        // Update existing target
                        updatedTargets = [...targets];
                        updatedTargets[editingIndex] = newTarget;
                    } else {
                        // Add new target
                        updatedTargets = [...targets, newTarget];
                    }

                    // Refresh the data
                    await refetch();

                    // Reset form state
                    setTargets(updatedTargets);
                    resetForm();
                } else {
                    message.error(response.data.saveKpiMasterDetails.message || "Failed to save target funnel.");
                }
            } catch (err) {
                console.error("API Error:", err);
            }
        }
    };

    const handleEdit = (index) => {
        const target = targets[index];
        setTargetName(target.name);
        setMonthlyTarget(target.monthly);
        setColor(target.color);
        setEditingIndex(index);
        setIsAddingNewTarget(true);
    };

    const handleDelete = async (index) => {
        const targetToDelete = targets[index];

        try {
            // Format color properly for API
            const colorCode = formatColorForAPI(targetToDelete.color);

            const response = await saveKpiMasterDetails({
                variables: {
                    accountId: accountId,
                    roleId: roleId,
                    kpiId: targetToDelete.id,
                    kpiName: targetToDelete.name,
                    kpiDefaultTarget: targetToDelete.monthly,
                    kpiColorCode: colorCode,
                    status: 0 // Set status to 0 for deletion
                },
            });

            if (response.data.saveKpiMasterDetails.success) {
                // Update local state after deletion
                const updatedTargets = targets.filter((_, i) => i !== index);
                setTargets(updatedTargets);

                // Refresh the data
                await refetch();
            } else {
                message.error(response.data.saveKpiMasterDetails.message || "Failed to delete target funnel.");
            }
        } catch (err) {
            console.error("API Error:", err);
        }
    };

    const handleNext = () => {
        // Validate both target funnels and lead indicators
        if (!targets.length) {
            message.error("Please configure at least one Target Funnel before proceeding.");
            return;
        }

        if (!leadIndicators.length) {
            message.error("Please configure at least one Lead Indicator before proceeding.");
            return;
        }

        // Proceed to the next step if applicable
        if (currentStep < steps.length - 1) {
            setCurrentStep(prevStep => prevStep + 1);
        }

        // Pass data to preview component through parent's onNext handler
        onNext({
            name: targetName,
            monthly: monthlyTarget,
            targets: targets,
            leadIndicators: leadIndicators
        });
    };

    const targetForm = (isEditing) => (
        <div className="flex flex-col gap-2.5 rounded-[8px] border border-[#ddd] bg-white p-[15px]">
            <input
                type="text"
                placeholder="Target Funnel Name"
                className="w-full rounded-[5px] border border-[#ccc] p-2 outline-none"
                value={targetName}
                onChange={(e) => setTargetName(e.target.value)}
            />
            {errors.targetName && <span className="block text-[11px] text-[#CC0000]">{errors.targetName}</span>}

            <div className="flex items-center justify-between gap-2">
                <div className="flex flex-col items-center gap-[5px]">
                    <label className="text-[14px]">Color</label>
                    <input
                        type="color"
                        className="rl-funnel-color-input h-[25px] w-[25px] cursor-pointer appearance-none overflow-hidden rounded-full border border-[#ccc] p-[2px]"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                    />
                </div>
                <div className="flex flex-col items-center gap-[5px]">
                    <label className="text-[14px]">Monthly Target</label>
                    <input
                        type="number"
                        min="0"
                        placeholder="0000"
                        className="rl-funnel-num-input h-[40px] w-[100px] rounded-[8px] border border-[#ccc] p-2 text-center outline-none"
                        value={monthlyTarget}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value.length <= 4) {
                                setMonthlyTarget(value);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddOrUpdate();
                            }
                        }}
                    />
                    {errors.monthlyTarget && (
                        <span className="block text-[11px] text-[#CC0000]">{errors.monthlyTarget}</span>
                    )}
                </div>
            </div>

            <div className="mt-[10px] flex items-center justify-between gap-2.5">
                <button
                    className="flex h-[40px] items-center justify-center rounded-[5px] border border-[#c00] bg-white px-[15px] text-[14px] font-medium text-[#c00]"
                    onClick={resetForm}
                >
                    Discard
                </button>
                <button
                    className={`flex h-[40px] items-center justify-center rounded-[5px] px-[15px] text-[14px] font-medium text-white ${targetName && monthlyTarget ? "bg-[#c00] cursor-pointer" : "bg-[#ababab] text-[#e2e0e0] cursor-not-allowed"
                        }`}
                    onClick={handleAddOrUpdate}
                    disabled={!targetName || !monthlyTarget || loading}
                >
                    {loading ? <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></span> : (isEditing ? "Save" : "Add")}
                </button>
            </div>
        </div>
    );

    return (
        <div className="w-full pb-5">
            <style>{scopedStyles}</style>
            <div className="flex flex-col gap-2.5">
                <div className="overflow-hidden rounded-[5px] border border-[#ddd] bg-white">
                    <button
                        className="flex w-full cursor-pointer items-center justify-between p-[15px] text-left text-[16px] font-medium"
                        onClick={() => setIsTargetCollapsed(!isTargetCollapsed)}
                    >
                        <span>Target Funnel Configuration</span>

                        <span className="text-[12px] font-normal leading-none text-black">
                            {isTargetCollapsed ? <DownOutlined /> : <UpOutlined />}
                        </span>
                    </button>

                    {!isTargetCollapsed && (
                        <div className="border-t border-[#ddd] p-[15px]">
                            <div className="rl-funnel-scroll w-full overflow-x-auto overflow-y-hidden pb-2">
                                <div className="flex min-w-max flex-row gap-4">
                                    {targets.map((target, index) => (
                                        editingIndex === index ? (
                                            <div key={index}>{targetForm(true)}</div>
                                        ) : (

                                            <div
                                                key={index}
                                                   className="flex h-[135px] w-[200px] flex-col rounded-[8px] border border-[#E0E0E0] bg-white p-[15px] shadow-[0_1px_4px_rgba(0,0,0,0.10)]"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span
                                                        title={target.name}
                                                        className="block max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap"
                                                    >
                                                        {target.name}
                                                    </span>
                                                    <div className="h-[20px] w-[20px]  rounded-full" style={{ backgroundColor: target.color }}></div>
                                                </div>
                                                <div className="mt-[10px] flex items-center justify-between">
                                                    <div className="flex text-[13px]">
                                                        <span>Monthly Target</span>
                                                    </div>
                                                    <strong className="font-semibold">{target.monthly}</strong>
                                                </div>
                                                <div className="mt-auto flex items-center justify-between gap-2.5">
                                                    <img className="h-[28px] w-[28px] cursor-pointer" src={Edit} onClick={() => handleEdit(index)} alt="Edit" />
                                                    <img className="h-[16px] w-[15px] cursor-pointer" src={Delete} onClick={() => handleDelete(index)} alt="Delete" />
                                                </div>
                                            </div>
                                        )
                                    ))}

                                    {isAddingNewTarget && editingIndex === null && targetForm(false)}

                                    {!isAddingNewTarget && editingIndex === null && (
                                        <button
                                            className={
                                                targets.length === 0
                                                    ? "h-[56px] w-[212px] mb-24 shrink-0 cursor-pointer self-center rounded-[4px] border border-[#AD0101] bg-[#AD0101] text-[16px] font-medium text-white"
                                                    : "h-[36px] min-w-[88px] mb-24 shrink-0 cursor-pointer self-center rounded-[4px] border border-[#AD0101] bg-[#AD0101] px-3 text-[16px] font-medium text-white"
                                            }
                                            onClick={() => setIsAddingNewTarget(true)}
                                        >
                                            {targets.length === 0 ? "New Target Funnel" : "Add"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    <p className="ml-[2%] pb-3 text-[12px] text-[#888]">The items displayed from left to right are arranged vertically from bottom to top within the funnel.</p>
                </div>

                <RoleLeadIndicatorConfiguration
                    roleId={roleId}
                    accountId={accountId}
                    onIndicatorsChange={handleLeadIndicatorsChange}
                />
            </div >

            <div className="mt-5 flex w-full justify-end">
                <button
                    className="flex h-[42px] w-[80px] cursor-pointer items-center justify-center rounded-[6px] bg-[#ad0101] text-[15px] font-normal text-white transition-colors duration-300 hover:bg-[#8C0101]"
                    onClick={handleNext}
                    disabled={currentStep === steps.length - 1}
                >
                    Next
                </button>
            </div>
        </div >
    );
};

export default RoleTargetFunnelConfiguration;
